import { useEffect, useState } from "react";
import { SearchableDropdown } from "../shared/SearchableDropdown";
import { upsertCommerceDimTableItem } from "../services/dimension-service";
import toast from "react-hot-toast";
import { useGetToken } from "../hooks/custom-hooks";
import { parseNewDropdownValue } from "../utilities/commerceConfig";

export const AddDimensionModal = ({
  isOpen,
  config,
  onClose
}) => {
  const [values, setValues] = useState({});
  const token = useGetToken();

  useEffect(() => {
    if (!isOpen || !config) return;
    const initialValues = {};

    config.fields?.forEach((field) => {
      if (field.type === "multiSelectDropdown") {
        initialValues[field.key] = [];
        return;
      }

      if (field.type === "boolean") {
        initialValues[field.key] = false;
        return;
      }

      initialValues[field.key] = "";
    });

    if (!config.autoGenerateLabel) {
      initialValues.label = "";
    }

    setValues(initialValues);
  }, [isOpen, config]);

  if (!isOpen || !config) return null;

  const handleChange = (key, value) => {
    setValues((prev) => {
      const next = {
        ...prev,
        [key]: value
      };

      config.fields?.forEach((field) => {
        if (
          field.visibleWhenField === key &&
          value !== field.visibleWhenValue
        ) {
          next[field.key] =
            field.type === "multiSelectDropdown" ? [] : "";
        }
      });

      return next;
    });
  };

  const toArray = (value) => {
    if (value === null || value === undefined || value === "") {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  };

  const normalizeObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === "string" && value.trim() === ""
          ? null
          : value
      ])
    );

  const getGeneratedLabel = () => {
    if (!config.autoGenerateLabel) {
      return values.label ?? "";
    }

    return config.autoGenerateLabel(values);
  };

  const isFieldVisible = (field) => {
    if (!field.visibleWhenField) {
      return true;
    }

    return (
      values[field.visibleWhenField] ===
      field.visibleWhenValue
    );
  };

  const visibleFields =
    config.fields?.filter(isFieldVisible) ?? [];

  const standardFields = visibleFields.filter(
    (field) =>
      field.type !== "multiSelectDropdown" ||
      field.showInSidebar !== true
  );

  const relationshipFields = visibleFields.filter(
    (field) =>
      field.type === "multiSelectDropdown" &&
      field.showInSidebar === true
  );

  const hasRelationshipFields =
    relationshipFields.length > 0;

  const handleUpsert = () => {
    const invalidField = config.fields?.find((field) => {
      if (!field.visibleWhenField) {
        return false;
      }

      const shouldBeVisible =
        values[field.visibleWhenField] ===
        field.visibleWhenValue;

      const value = values[field.key];

      const isEmpty =
        value === null ||
        value === undefined ||
        value === "";

      return shouldBeVisible && isEmpty;
    });

    if (invalidField) {
      toast.error(`${invalidField.label} is required.`);
      return;
    }

    const fields = {};
    const relationships = [];

    config.fields?.forEach((field) => {
      if (!isFieldVisible(field)) {
        return;
      }

      let value = values[field.key];

      if (field.type === "boolean" && value === "") {
        value = false;
      }

      if (field.type === "multiSelectDropdown") {
        relationships.push(
          ...(Array.isArray(value)
            ? value
            : value
              ? [value]
              : [])
        );
        return;
      }

      const newValue = parseNewDropdownValue(value);

      if (newValue) {
        fields[field.key] = null;

        const displayField =
          field.displayField ??
          field.labelField ??
          field.key.replace(/Key$/, "");

        fields[displayField] = newValue.label;
        return;
      }

      fields[field.key] = value?.value ?? value;
    });

    if (config.labelField) {
      fields[config.labelField] = getGeneratedLabel();
    }

    const payload = {
      entity: config.entity,
      tableName: config.tableName,
      primaryKey: config.primaryKey,
      rows: [
        {
          fields: normalizeObject(fields),
          relationships
        }
      ]
    };

    updateDim(payload);
  };

  const updateDim = async (payload) => {
    const response = await upsertCommerceDimTableItem(payload, token);

    if (response.data) {
      toast.success("Dimension Created.");
      onClose(true);
    } else {
      toast.error("The dimension did not save. Please refresh the page and try again.");
    }
  };

  return (
    <div className="dim-modal-overlay">
      <div className="dim-modal dim-modal-large">
        <button
          className="dim-modal-close"
          onClick={onClose}
        >
          x
        </button>
        <div className="dim-modal-header">
          <h2>{config.title}</h2>
          <p>{config.description}</p>
        </div>
        <div className="dim-modal-content dim-modal-content--single">
          <div className="dim-modal-main">
            <section className="dim-modal-card">
              <label className="dim-section-label">
                {config.displayLabel}
              </label>
              {config.autoGenerateLabel ? (
                <div>
                  <div className="dim-preview-field">
                    {getGeneratedLabel() || ""}
                  </div>
                  <p className="dim-helper-text">Name will generate automatically.</p>
                </div>
              ) : (
                <div>
                  <input
                    className="dim-text-input"
                    placeholder={config.displayPlaceholder}
                    value={values.label ?? ""}
                    onChange={(e) => handleChange("label", e.target.value)}
                  />
                  <p className="dim-helper-text">Enter the display name.</p>
                </div>
              )}
              <div className="dim-divider" />

              <div className="dim-field-grid">
                {standardFields.map((field) => {
                  if (field.key === "label") {
                    return null;
                  }
                  if (field.type === "boolean") {
                    return (
                      <label
                        key={field.key}
                        className="dim-choice-card"
                      >
                        <input
                          type="checkbox"
                          checked={!!values[field.key]}
                          onChange={(e) =>handleChange(field.key, e.target.checked)}
                        />
                        <div>
                          <strong>{field.label}</strong>
                          {field.description && (
                            <p>{field.description}</p>
                          )}
                        </div>
                      </label>
                    );
                  }

                  if (field.type === "dropdown" || field.type === "text" || field.type === "lookup"
                  ) {
                    return (
                      <SearchableDropdown
                        key={field.key}
                        className="searchable-dropdown dark-dropdown"
                        id={field.key}
                        label={field.label}
                        value={values[field.key] ?? ""}
                        dropdownValues={field.lookup ?? []}
                        defaultPhrase={field.placeholder}
                        multiple={false}
                        createdLabel={field.displayField
                            ? values[field.displayField]
                            : null
                        }
                        handleDropdownChange={(e) => {
                          handleChange(
                            field.key,
                            e.target.value
                          );
                          if (e.target.createdLabel && field.key.endsWith("Key")) {
                            handleChange(field.key.replace(/Key$/,""), e.target.createdLabel);
                          }
                        }}
                        creatable={field.creatable === true}
                      />
                    );
                  }
                  if (field.type === "multiSelectDropdown") {
                    return (
                      <SearchableDropdown
                        key={field.key}
                        className="searchable-dropdown dark-dropdown"
                        id={field.key}
                        label={field.label}
                        value={values[field.key] ?? []}
                        dropdownValues={field.lookup ?? []}
                        defaultPhrase={field.placeholder}
                        multiple
                        handleDropdownChange={(e) =>
                          handleChange(field.key, e.target.value)
                        }
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </section>
          </div>
        </div>
        <div className="dim-modal-actions">
          <button
            className="dim-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="dim-btn-primary"
            onClick={handleUpsert}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};